import React, { useState, useEffect } from 'react';
import { Cloud, CloudRain, Sun, Wind, Droplets, Eye } from 'lucide-react';

const WeatherWidget = () => {
  const [currentWeather, setCurrentWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);

  // Using a free weather API (Open-Meteo) - no API key required
  // Yelagiri Hills coordinates
  const latitude = 12.582816;
  const longitude = 78.628008;

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      // Fetch current weather and 10-day forecast
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=Asia/Kolkata&forecast_days=11`
      );
      const data = await response.json();

      // Set current weather
      setCurrentWeather({
        temperature: Math.round(data.current.temperature_2m),
        humidity: data.current.relative_humidity_2m,
        windSpeed: Math.round(data.current.wind_speed_10m),
        weatherCode: data.current.weather_code
      });

      // Set 10-day forecast (skip today, get next 10 days)
      const forecastData = data.daily.time.slice(1, 11).map((date, index) => ({
        date: new Date(date),
        maxTemp: Math.round(data.daily.temperature_2m_max[index + 1]),
        minTemp: Math.round(data.daily.temperature_2m_min[index + 1]),
        weatherCode: data.daily.weather_code[index + 1]
      }));
      setForecast(forecastData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching weather:', error);
      setLoading(false);
    }
  };

  const getWeatherIcon = (code, size = 24) => {
    // Weather code mapping (WMO Weather interpretation codes)
    if (code === 0 || code === 1) return <Sun size={size} />;
    if (code === 2 || code === 3) return <Cloud size={size} />;
    if (code >= 51 && code <= 67) return <CloudRain size={size} />;
    if (code >= 80 && code <= 99) return <CloudRain size={size} />;
    return <Cloud size={size} />;
  };

  const getWeatherDescription = (code) => {
    if (code === 0) return 'Clear Sky';
    if (code === 1 || code === 2) return 'Partly Cloudy';
    if (code === 3) return 'Overcast';
    if (code >= 51 && code <= 67) return 'Rainy';
    if (code >= 80 && code <= 99) return 'Showers';
    return 'Cloudy';
  };

  const getDayName = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  const getDateNum = (date) => {
    return date.getDate();
  };

  if (loading) {
    return (
      <div className="weather-widget loading">
        <div className="weather-loader">Loading weather...</div>
      </div>
    );
  }

  return (
    <div className="weather-widget">
      <div className="weather-header">
        <h3 className="weather-title">Weather at Luxtansa</h3>
        <p className="weather-location">Current Conditions</p>
      </div>

      {/* Current Weather */}
      {currentWeather && (
        <div className="weather-current">
          <div className="weather-current-main">
            <div className="weather-icon-large">
              {getWeatherIcon(currentWeather.weatherCode, 28)}
            </div>
            <div className="weather-temp-section">
              <div className="weather-temp-current">{currentWeather.temperature}°C</div>
              <div className="weather-description">
                {getWeatherDescription(currentWeather.weatherCode)}
              </div>
            </div>
          </div>

          <div className="weather-details">
            <div className="weather-detail-item">
              <Droplets size={16} />
              <span>{currentWeather.humidity}%</span>
              <span className="detail-label">Humidity</span>
            </div>
            <div className="weather-detail-item">
              <Wind size={16} />
              <span>{currentWeather.windSpeed} km/h</span>
              <span className="detail-label">Wind</span>
            </div>
          </div>
        </div>
      )}

      {/* 10-Day Forecast */}
      <div className="weather-forecast">
        <h4 className="forecast-title">10-Day Forecast</h4>
        <div className="forecast-grid">
          {forecast.map((day, index) => (
            <div key={index} className="forecast-day">
              <div className="forecast-day-name">{getDayName(day.date)}</div>
              <div className="forecast-date">{getDateNum(day.date)}</div>
              <div className="forecast-icon">
                {getWeatherIcon(day.weatherCode, 20)}
              </div>
              <div className="forecast-temps">
                <span className="forecast-temp-max">{day.maxTemp}°</span>
                <span className="forecast-temp-min">{day.minTemp}°</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;
